/**
 * The red dots on the nav — "there is something here for you".
 *
 * `shell.ts` has had a working `setNavDot(path, on)` and a hidden `[data-dot]` span on every
 * nav entry since the chrome was built, and outside `shell.test.ts` **nothing ever called it**.
 * The visual-language brief names the dots specifically ("a real part of the language — they
 * say the menu is alive", §4), and the menu was not saying it: a claimable daily reward and a
 * quest ready to hand in were both invisible from eight of the nine screens.
 *
 * They are a store rather than a direct call because the two things that know are a route and
 * a panel, both of which mount *inside* the shell and neither of which can reach the closure
 * `setNavDot` lives in. The shell subscribes once at mount; anything that learns something
 * publishes. Same shape as `player-progress-store`, and for the same reason.
 *
 * **Only ever set from a real fetch.** A dot that is on when there is nothing to collect is
 * worse than no dot at all — it trains the player to ignore the one signal the menu has.
 */
import { createStore } from '../../core/state/store';

export interface NavBadges {
  /** A daily-streak day is claimable right now. */
  daily: boolean;
  /** At least one quest is finished and waiting to be handed in. */
  quests: boolean;
}

export const navBadgesStore = createStore<NavBadges>({ daily: false, quests: false });

/** Publishes one field without disturbing the other — the two have different owners. */
export function setNavBadge(key: keyof NavBadges, on: boolean): void {
  const current = navBadgesStore.get();
  if (current[key] === on) return;
  navBadgesStore.set({ ...current, [key]: on });
}
